// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import eigentAppIconBlack from '@/assets/logo/icon_black.svg';
import eigentMarkDark from '@/assets/logo/icon_mark_dark.svg';
import eigentMarkLight from '@/assets/logo/icon_mark_light.svg';
import { Button } from '@/components/ui/button';
import { MorphIcon } from '@/components/ui/morph-icon';
import { useIsDesktop } from '@/hooks/useMediaQuery';
import { useRemoteHistory } from '@/hooks/useRemoteHistory';
import { cn } from '@/lib/utils';
import {
  applyRemoteControlProjectRun,
  createRemoteControlProject,
  discardRemoteControlProjectOverlays,
  extendRemoteControlSession,
  getRemoteArtifactDownload,
  getRemoteControlCommandState,
  getRemoteControlEventSocketUrl,
  getRemoteControlSession,
  getRemoteControlTarget,
  isRemoteControlTargetReady,
  listRemoteControlOverlays,
  listRemoteControlProjects,
  patchRemoteControlTarget,
  refreshRemoteControlProject,
  RemoteCommandProjection,
  RemoteControlError,
  RemoteControlProject,
  RemoteControlSession,
  RemoteControlSpace,
  RemoteControlStep,
  RemoteControlTarget,
  revokeRemoteControlSession,
  sendRemoteControlCommand,
} from '@web/api/remoteControl';
import { RemoteHumanInteractionCard } from '@web/components/remote/RemoteHumanInteractionCard';
import { RemoteInputBox } from '@web/components/remote/RemoteInputBox';
import { RemoteSettingsPanel } from '@web/components/remote/RemoteSettingsPanel';
import { RemoteSidePanel } from '@web/components/remote/RemoteSidePanel';
import { RunTimeline } from '@web/components/remote/timeline/RunTimeline';
import { useKeyboardInset } from '@web/hooks/useKeyboardInset';
import {
  selectLatestExpiredHumanInteraction,
  selectPendingHumanInteraction,
  selectPendingLegacyAsk,
  type ProjectedArtifact,
} from '@web/lib/projector';
import { buildActivityTimeline } from '@web/lib/remoteActivityModel';
import {
  clearRemoteAccessToken,
  getRemoteAccessToken,
  REMOTE_LOGIN_REQUIRED_CODE,
  REMOTE_LOGIN_USER_MISMATCH_CODE,
  startRemoteLoginRedirect,
} from '@web/lib/remoteAuth';
import {
  commandProjectionStatus,
  commandReconcileNextAttemptAt,
  durablyResolvedHumanReplyIds,
  durablyResolvedInteractionIds,
  INTERACTION_DECISION_PENDING_TIMEOUT_MS,
  interactionDecisionIsPending,
  interactionDecisionTerminalProblem,
  interactionDecisionTimedOut,
  latestInteractionDecisionCommand,
  latestSupersedableInteractionDecisionCommand,
  loadRemoteCommandHistory,
  mergeRemoteCommand,
  saveRemoteCommandHistory,
  scheduleCommandReconciliation,
  watermarkMatchesProject,
  type RemoteCommandDisplay,
} from '@web/lib/remoteCommandProjection';
import {
  parseRemoteControlToken,
  scrubRemoteControlTokenFromUrl,
} from '@web/lib/remoteControlToken';
import { resolveRemoteHumanInputReplyTarget } from '@web/lib/remoteHumanInputTarget';
import type { HumanInputItem } from '@web/lib/remoteWorkItemModel';
import { useAuthStore } from '@web/store/authStore';
import { Loader2, LogIn, Menu, RefreshCw, Settings } from 'lucide';
import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

type CommandStatus = RemoteCommandDisplay;

type RemotePageError = {
  message: string;
  status?: number;
  code?: string | null;
};

const WS_PING_INTERVAL_MS = 30_000;
const WS_PONG_TIMEOUT_MS = 75_000;
const SWITCH_TARGET_TIMEOUT_MS = 60_000;
const DISMISSED_EXPIRED_PREFIX = 'eigent:remote-control-dismissed-expired:v1:';
const TERMINAL_COMMAND_STATUSES = new Set([
  'completed',
  'failed',
  'rejected',
  'outcome_unknown',
]);
const FAILED_COMMAND_STATUSES = new Set([
  'failed',
  'rejected',
  'expired',
  'outcome_unknown',
]);

function toPageError(err: unknown, fallback: string): RemotePageError {
  if (err instanceof RemoteControlError) {
    return {
      message: err.message || fallback,
      status: err.status,
      code: err.code,
    };
  }
  if (err instanceof Error) {
    return { message: err.message || fallback };
  }
  return { message: fallback };
}

function isRemoteLoginRequired(error: RemotePageError): boolean {
  return error.code === REMOTE_LOGIN_REQUIRED_CODE || error.status === 401;
}

function isRemoteLoginMismatch(error: RemotePageError): boolean {
  return error.code === REMOTE_LOGIN_USER_MISMATCH_CODE;
}

function errorHint(error: RemotePageError): string {
  if (isRemoteLoginMismatch(error)) {
    return 'This remote link belongs to a different Eigent account. Sign in with the desktop owner account, or ask the owner to generate a new link.';
  }
  if (isRemoteLoginRequired(error)) {
    return 'Sign in to the Eigent account that owns this desktop session to continue.';
  }
  if (error.status === 429) {
    return 'Too many remote commands were sent too quickly. Wait a few seconds, then try again.';
  }
  if (error.status === 403 || error.status === 404) {
    return 'The link is invalid or has expired. Ask the desktop owner to generate a new one.';
  }
  if (error.status === 410) {
    return 'This remote session has been revoked or has expired.';
  }
  if (error.code === 'BRIDGE_OFFLINE') {
    return 'The desktop bridge is offline. Keep Eigent running on the original computer.';
  }
  if (error.status && error.status >= 500) {
    return 'The Eigent server is having trouble. Try again in a moment.';
  }
  return 'Check your network connection and try again.';
}

function asRecord(value: unknown): Record<string, any> {
  return value && typeof value === 'object'
    ? (value as Record<string, any>)
    : {};
}

function getAskAgent(step: RemoteControlStep | null): string {
  return String(asRecord(step?.data).agent || '');
}

function getAskText(step: RemoteControlStep | null): string {
  const data = asRecord(step?.data);
  return String(
    data.content ||
      data.notice ||
      data.answer ||
      data.question ||
      (typeof step?.data === 'string' ? step.data : '')
  );
}

function loadDismissedExpired(sessionId: string): Set<string> {
  try {
    const value = JSON.parse(
      window.localStorage.getItem(`${DISMISSED_EXPIRED_PREFIX}${sessionId}`) ||
        '[]'
    );
    return new Set(
      Array.isArray(value)
        ? value.filter((item) => typeof item === 'string')
        : []
    );
  } catch {
    return new Set();
  }
}

function saveDismissedExpired(sessionId: string, values: Set<string>): void {
  try {
    window.localStorage.setItem(
      `${DISMISSED_EXPIRED_PREFIX}${sessionId}`,
      JSON.stringify([...values].slice(-128))
    );
  } catch {
    // Dismissal persistence is best-effort and never blocks remote control.
  }
}

export default function RemoteControlPage() {
  const { sessionId = '' } = useParams();
  const [linkToken] = useState(() => {
    const token = parseRemoteControlToken(
      window.location.search,
      window.location.hash
    );
    scrubRemoteControlTokenFromUrl();
    return token;
  });
  const [session, setSession] = useState<RemoteControlSession | null>(null);
  const [space, setSpace] = useState<RemoteControlSpace | null>(null);
  const [projects, setProjects] = useState<RemoteControlProject[]>([]);
  const [commands, setCommands] = useState<CommandStatus[]>(() =>
    loadRemoteCommandHistory(sessionId)
  );
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [projectLoading, setProjectLoading] = useState(false);
  const [controlLoading, setControlLoading] = useState<string | null>(null);
  const [error, setError] = useState<RemotePageError | null>(null);
  const [reloading, setReloading] = useState(false);
  const [answeredAskStepIds, setAnsweredAskStepIds] = useState<
    Set<number | string>
  >(() => new Set());
  const [selectedHumanInteractionId, setSelectedHumanInteractionId] = useState<
    string | null
  >(null);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
  const [decisionClock, setDecisionClock] = useState(() => Date.now());
  const [dismissedExpiredInteractionIds, setDismissedExpiredInteractionIds] =
    useState<Set<string>>(() => loadDismissedExpired(sessionId));
  const isDesktop = useIsDesktop();
  const appearance = useAuthStore((state) => state.appearance);
  const appIcon = appearance === 'dark' ? eigentMarkDark : eigentMarkLight;

  // Keep bottom-anchored input above the on-screen keyboard on mobile.
  useKeyboardInset();

  const sendingRef = useRef(false);
  const targetRef = useRef<RemoteControlTarget | null>(null);
  const eventSocketRef = useRef<WebSocket | null>(null);
  const commandsRef = useRef<CommandStatus[]>(commands);
  const humanReplyInteractionByCommandRef = useRef<Map<string, string>>(
    new Map()
  );
  const failedCommandStatusByIdentityRef = useRef<Map<string, string>>(
    new Map()
  );
  const commandSessionRef = useRef(sessionId);
  const commandSyncInFlightRef = useRef<Promise<void> | null>(null);
  const commandReconcileFailuresRef = useRef(
    new Map<string, { attempts: number; nextAttemptAt: number }>()
  );
  const switchTimeoutRef = useRef<number | null>(null);
  const authRedirectingRef = useRef(false);
  const history = useRemoteHistory(sessionId, linkToken);

  useEffect(() => {
    setDismissedExpiredInteractionIds(loadDismissedExpired(sessionId));
  }, [sessionId]);

  useEffect(() => {
    if (commandSessionRef.current !== sessionId) {
      const restored = loadRemoteCommandHistory(sessionId);
      commandSessionRef.current = sessionId;
      commandsRef.current = restored;
      humanReplyInteractionByCommandRef.current.clear();
      failedCommandStatusByIdentityRef.current.clear();
      setCommands(restored);
      return;
    }
    commandsRef.current = commands;
    saveRemoteCommandHistory(sessionId, commands);
  }, [commands, sessionId]);

  const restoreFailedHumanReply = useCallback(
    ({
      commandId,
      clientRequestId,
      humanInteractionId,
      status,
    }: {
      commandId: string;
      clientRequestId?: string | null;
      humanInteractionId?: string | null;
      status: string;
    }) => {
      failedCommandStatusByIdentityRef.current.set(commandId, status);
      if (clientRequestId) {
        failedCommandStatusByIdentityRef.current.set(clientRequestId, status);
      }
      while (failedCommandStatusByIdentityRef.current.size > 64) {
        const oldestKey = failedCommandStatusByIdentityRef.current
          .keys()
          .next().value;
        if (!oldestKey) break;
        failedCommandStatusByIdentityRef.current.delete(oldestKey);
      }
      void humanInteractionId;
    },
    []
  );

  const reconcileCommandStates = useCallback(
    (force = false) => {
      const runReconciliation = () => {
        const now = Date.now();
        const commandIds = commandsRef.current
          .filter(
            (command) =>
              !TERMINAL_COMMAND_STATUSES.has(command.status) &&
              !(command.status === 'expired' && command.lateResult) &&
              (force ||
                (commandReconcileFailuresRef.current.get(command.id)
                  ?.nextAttemptAt ?? 0) <= now)
          )
          .map((command) => command.id);
        if (!commandIds.length) return Promise.resolve();

        const operation = Promise.allSettled(
          commandIds.map((commandId) =>
            getRemoteControlCommandState(sessionId, linkToken, commandId)
          )
        )
          .then((results) => {
            results.forEach((result, index) => {
              const commandId = commandIds[index];
              if (result.status !== 'fulfilled') {
                const previous =
                  commandReconcileFailuresRef.current.get(commandId);
                const attempts = Math.min((previous?.attempts ?? 0) + 1, 8);
                commandReconcileFailuresRef.current.set(commandId, {
                  attempts,
                  nextAttemptAt: commandReconcileNextAttemptAt(attempts),
                });
                return;
              }
              commandReconcileFailuresRef.current.delete(commandId);
              const projection = result.value;
              const status = commandProjectionStatus(projection);
              if (FAILED_COMMAND_STATUSES.has(status)) {
                restoreFailedHumanReply({
                  commandId,
                  clientRequestId: projection.client_request_id,
                  humanInteractionId: projection.human_interaction_id,
                  status,
                });
              }
              setCommands((current) =>
                mergeRemoteCommand(current, {
                  id: commandId,
                  projectId: projection.project_id,
                  runId: projection.run_id,
                  clientRequestId: projection.client_request_id,
                  humanInteractionId: projection.human_interaction_id,
                  content: 'Remote command',
                  type: 'unknown',
                  status,
                  lateResult: Boolean(projection.late_result),
                  lastSyncedAt: new Date().toISOString(),
                  error: projection.integrity_alert || undefined,
                  projection,
                })
              );
            });
          })
          .finally(() => {
            if (commandSyncInFlightRef.current === operation) {
              commandSyncInFlightRef.current = null;
            }
          });
        commandSyncInFlightRef.current = operation;
        return operation;
      };

      const inFlight = commandSyncInFlightRef.current;
      return scheduleCommandReconciliation(inFlight, force, runReconciliation);
    },
    [linkToken, restoreFailedHumanReply, sessionId]
  );

  const clearSwitchTimeout = useCallback(() => {
    if (switchTimeoutRef.current !== null) {
      window.clearTimeout(switchTimeoutRef.current);
      switchTimeoutRef.current = null;
    }
  }, []);

  const startRemoteSignIn = useCallback(() => {
    try {
      authRedirectingRef.current = true;
      setLoading(true);
      startRemoteLoginRedirect(linkToken);
    } catch (err) {
      authRedirectingRef.current = false;
      setError(toPageError(err, 'Failed to start remote control sign-in.'));
      setLoading(false);
    }
  }, [linkToken]);

  const handleRemoteAuthError = useCallback(
    (pageError: RemotePageError): boolean => {
      if (isRemoteLoginRequired(pageError)) {
        setError({
          ...pageError,
          message:
            pageError.message ||
            'Sign in to continue using this remote control link.',
        });
        setLoading(false);
        return true;
      }

      if (isRemoteLoginMismatch(pageError)) {
        clearRemoteAccessToken(sessionId);
        setError({
          ...pageError,
          message:
            pageError.message ||
            'This remote link belongs to a different Eigent account.',
        });
        setLoading(false);
        return true;
      }

      return false;
    },
    [sessionId]
  );

  const target = useMemo(
    () => (session ? getRemoteControlTarget(session) : null),
    [session]
  );
  targetRef.current = target;

  const bridgeOnline =
    session?.status === 'active' && session?.bridge_status === 'online';
  const sessionReady = Boolean(session);
  const ready =
    bridgeOnline && !projectLoading && isRemoteControlTargetReady(target);
  const activeProject = useMemo(
    () => projects.find((project) => project.id === target?.project_id) || null,
    [projects, target?.project_id]
  );
  const folderBacked = space?.source_type === 'folder';
  const lastCommand = useMemo(() => commands.slice().reverse()[0], [commands]);
  const effectiveProjectView = history.projectView;
  const legacyPendingAsk = useMemo(() => {
    if (!effectiveProjectView) return null;
    const ask = selectPendingLegacyAsk(
      effectiveProjectView,
      answeredAskStepIds
    );
    return ask
      ? {
          step_id: ask.stepId,
          task_id: ask.taskId,
          project_id: ask.projectId,
          step: ask.step,
          data: ask.data,
          timestamp: ask.timestamp,
        }
      : null;
  }, [answeredAskStepIds, effectiveProjectView]);
  const historyFreshness = useMemo(() => {
    if (!history.projectView) return 'History loading';
    if (history.projectView.needsResync) return 'History resyncing';
    return `History cursor ${history.projectView.currentCursor}${
      history.projectView.eventsTruncated ? ' · partial' : ''
    }`;
  }, [history.projectView]);
  const commandFreshness = useMemo(() => {
    if (!lastCommand) return 'No remote command';
    if (lastCommand.lateResult) return 'Command result received late';
    return `Command ${lastCommand.status}`;
  }, [lastCommand]);
  const resolvedInteractionIds = useMemo(
    () => durablyResolvedInteractionIds(commands),
    [commands]
  );
  const durablyAnsweredHumanInteractionIds = useMemo(
    () => durablyResolvedHumanReplyIds(commands),
    [commands]
  );
  const inFlightHumanReplyInteractionIds = useMemo(
    () =>
      new Set(
        commands
          .filter(
            (command) =>
              command.type === 'human_reply' &&
              !TERMINAL_COMMAND_STATUSES.has(command.status) &&
              command.humanInteractionId
          )
          .map((command) => command.humanInteractionId as string)
      ),
    [commands]
  );
  const pendingInteraction = useMemo(
    () =>
      history.projectView
        ? selectPendingHumanInteraction(
            history.projectView,
            resolvedInteractionIds,
            decisionClock
          )
        : null,
    [decisionClock, history.projectView, resolvedInteractionIds]
  );
  const expiredInteraction = useMemo(() => {
    const expired = history.projectView
      ? selectLatestExpiredHumanInteraction(history.projectView, decisionClock)
      : null;
    return expired && !dismissedExpiredInteractionIds.has(expired.interactionId)
      ? expired
      : null;
  }, [decisionClock, dismissedExpiredInteractionIds, history.projectView]);
  const latestInteractionCommand = useMemo(
    () =>
      pendingInteraction
        ? latestInteractionDecisionCommand(
            commands,
            pendingInteraction.interactionId,
            pendingInteraction.version,
            pendingInteraction.actionDigest
          )
        : null,
    [commands, pendingInteraction]
  );
  useEffect(() => {
    if (!pendingInteraction) return;
    setDecisionClock(Date.now());
    const deadlines: number[] = [];
    if (latestInteractionCommand?.createdAt !== undefined) {
      deadlines.push(
        latestInteractionCommand.createdAt +
          INTERACTION_DECISION_PENDING_TIMEOUT_MS
      );
    }
    if (pendingInteraction.expiresAt) {
      const expiresAt = Date.parse(pendingInteraction.expiresAt);
      if (Number.isFinite(expiresAt)) deadlines.push(expiresAt);
    }
    if (!deadlines.length) return;
    const remaining = Math.min(...deadlines) - Date.now();
    const timer = window.setTimeout(
      () => setDecisionClock(Date.now()),
      Math.max(0, remaining) + 20
    );
    return () => window.clearTimeout(timer);
  }, [
    latestInteractionCommand?.createdAt,
    pendingInteraction?.expiresAt,
    pendingInteraction?.interactionId,
  ]);
  const interactionDecisionPending = Boolean(
    pendingInteraction &&
    interactionDecisionIsPending(
      commands,
      pendingInteraction.interactionId,
      decisionClock,
      pendingInteraction.version,
      pendingInteraction.actionDigest
    )
  );
  const interactionDecisionRetryAvailable = Boolean(
    pendingInteraction &&
    interactionDecisionTimedOut(
      commands,
      pendingInteraction.interactionId,
      decisionClock,
      pendingInteraction.version,
      pendingInteraction.actionDigest
    )
  );
  const interactionDecisionProblem = pendingInteraction
    ? interactionDecisionTerminalProblem(
        commands,
        pendingInteraction.interactionId,
        pendingInteraction.version,
        pendingInteraction.actionDigest
      )
    : null;

  const activityTimeline = useMemo(
    () =>
      effectiveProjectView
        ? buildActivityTimeline(
            effectiveProjectView,
            commands,
            activeProject?.mode === 'workforce' ? 'workforce' : 'single-agent'
          )
        : null,
    [activeProject?.mode, commands, effectiveProjectView]
  );
  const openArtifact = useCallback(
    async (artifact: ProjectedArtifact) => {
      const chatFileId = artifact.assetRef?.chatFileId;
      if (!chatFileId) {
        toast.info('This file is still uploading from Desktop.');
        return;
      }
      try {
        const result = await getRemoteArtifactDownload(
          sessionId,
          linkToken,
          chatFileId
        );
        window.open(result.download_url, '_blank', 'noopener,noreferrer');
      } catch (artifactError) {
        toast.error(
          artifactError instanceof Error
            ? artifactError.message
            : 'Unable to open this file.'
        );
      }
    },
    [linkToken, sessionId]
  );
  const pendingAsk =
    legacyPendingAsk &&
    activityTimeline?.runs.find((run) => run.id === legacyPendingAsk.task_id)
      ?.normalizedTurn
      ? null
      : legacyPendingAsk;
  const pendingHumanInputs = useMemo(
    () =>
      (activityTimeline?.runs || [])
        .flatMap((run) => run.pendingHumanInputs)
        .filter(
          (item) => !durablyAnsweredHumanInteractionIds.has(item.interactionId)
        ),
    [activityTimeline, durablyAnsweredHumanInteractionIds]
  );
  const actionableHumanInputs = useMemo(
    () =>
      pendingHumanInputs.filter(
        (item) => !inFlightHumanReplyInteractionIds.has(item.interactionId)
      ),
    [inFlightHumanReplyInteractionIds, pendingHumanInputs]
  );
  const selectedHumanInput =
    actionableHumanInputs.find(
      (item) => item.interactionId === selectedHumanInteractionId
    ) || null;
  const replyTarget = resolveRemoteHumanInputReplyTarget(
    actionableHumanInputs,
    selectedHumanInteractionId,
    message
  );
  const failedHumanReplyTarget =
    lastCommand?.type === 'human_reply' && lastCommand.humanInteractionId
      ? pendingHumanInputs.find(
          (item) => item.interactionId === lastCommand.humanInteractionId
        ) || null
      : null;

  useEffect(() => {
    if (
      selectedHumanInteractionId &&
      !pendingHumanInputs.some(
        (item) => item.interactionId === selectedHumanInteractionId
      )
    ) {
      setSelectedHumanInteractionId(null);
    }
  }, [pendingHumanInputs, selectedHumanInteractionId]);

  const reloadSession = useCallback(async () => {
    if (!sessionId || !linkToken) {
      setError({ message: 'Remote control link is missing a token.' });
      setLoading(false);
      return;
    }
    setError(null);
    const [loadedSession, projectList] = await Promise.all([
      getRemoteControlSession(sessionId, linkToken),
      listRemoteControlProjects(sessionId, linkToken),
    ]);
    const loadedTarget = getRemoteControlTarget(loadedSession);
    if (loadedTarget.project_id) {
      await history.hydrate(loadedTarget.project_id);
    } else {
      history.reset(null);
    }
    targetRef.current = loadedTarget;
    setSession(loadedSession);
    setSpace(projectList.space);
    setProjects(projectList.items || []);
  }, [history.hydrate, history.reset, linkToken, sessionId]);

  const retryLoad = useCallback(async () => {
    if (reloading) return;
    setReloading(true);
    setLoading(true);
    try {
      await reloadSession();
    } catch (err) {
      const pageError = toPageError(
        err,
        'Failed to open remote control session.'
      );
      if (!handleRemoteAuthError(pageError)) {
        setError(pageError);
      }
    } finally {
      setReloading(false);
      if (!authRedirectingRef.current) {
        setLoading(false);
      }
    }
  }, [handleRemoteAuthError, reloadSession, reloading]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        await reloadSession();
      } catch (err) {
        if (!cancelled) {
          const pageError = toPageError(
            err,
            'Failed to open remote control session.'
          );
          if (!handleRemoteAuthError(pageError)) {
            setError(pageError);
          }
        }
      } finally {
        if (!cancelled && !authRedirectingRef.current) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [handleRemoteAuthError, reloadSession]);

  useEffect(() => {
    if (!sessionId || !linkToken || !session) return;
    let ws: WebSocket | null = null;
    let pingTimer: number | null = null;
    let watchdogTimer: number | null = null;
    let reconnectTimer: number | null = null;
    let reconnectAttempt = 0;
    let stopped = false;
    let lastPongAt = Date.now();
    let isReconnect = false;

    const armWatchdog = () => {
      if (watchdogTimer) window.clearInterval(watchdogTimer);
      watchdogTimer = window.setInterval(() => {
        if (Date.now() - lastPongAt > WS_PONG_TIMEOUT_MS) ws?.close();
      }, WS_PING_INTERVAL_MS);
    };

    const connect = () => {
      ws = new WebSocket(getRemoteControlEventSocketUrl(sessionId));
      eventSocketRef.current = ws;
      ws.onopen = () => {
        console.info('[RemoteControlPage][RC-TRACE] events ws open', {
          isReconnect,
          subscribed_project_id: targetRef.current?.project_id || null,
        });
        lastPongAt = Date.now();
        ws?.send(
          JSON.stringify({
            type: 'subscribe',
            link_token: linkToken,
            auth_token: getRemoteAccessToken(sessionId),
            subscribed_project_id: targetRef.current?.project_id || null,
            after_cursor: history.currentCursor(),
          })
        );
        if (isReconnect) {
          void history.syncDeltas().catch((error) => {
            console.warn(
              '[RemoteControlPage] reconnect delta sync failed',
              error
            );
          });
        }
        void reconcileCommandStates().catch((error) => {
          console.warn(
            '[RemoteControlPage] command reconciliation failed',
            error
          );
        });
        if (history.needsLegacySync()) {
          void history.syncLegacySteps().catch((error) => {
            console.warn(
              '[RemoteControlPage] legacy history sync failed',
              error
            );
          });
        }
        pingTimer = window.setInterval(() => {
          ws?.send(JSON.stringify({ type: 'ping' }));
        }, WS_PING_INTERVAL_MS);
        armWatchdog();
      };
      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          lastPongAt = Date.now();
          if (payload.type === 'error') {
            const pageError = {
              message:
                String(payload.message || '') ||
                'Remote control sign-in is required.',
              status:
                payload.code === REMOTE_LOGIN_USER_MISMATCH_CODE ? 403 : 401,
              code: typeof payload.code === 'string' ? payload.code : null,
            };
            stopped = true;
            ws?.close();
            handleRemoteAuthError(pageError);
            return;
          }
          if (payload.type === 'connected') {
            // A transport open does not prove that the subscription was
            // authenticated. Reset backoff only after the server accepts the
            // protocol handshake; otherwise an expired token can reconnect
            // forever without ever reaching the configured upper bound.
            reconnectAttempt = 0;
            setSession((current) =>
              current
                ? {
                    ...current,
                    bridge_status: payload.bridge_status,
                    current_project_id:
                      payload.current_project_id ?? current.current_project_id,
                    current_task_id:
                      payload.current_task_id ?? current.current_task_id,
                    current_history_id:
                      payload.current_history_id ?? current.current_history_id,
                    current_brain_session_id:
                      payload.current_brain_session_id ??
                      current.current_brain_session_id,
                  }
                : current
            );
            void history
              .ensureCursor(payload.current_cursor)
              ?.catch((error) => {
                console.warn(
                  '[RemoteControlPage] connected cursor recovery failed',
                  error
                );
              });
          }
          if (payload.type === 'canonical_event') {
            const needsResync = history.applyCanonicalEvents([payload]);
            if (needsResync) {
              void history.syncDeltas().catch((error) => {
                console.warn(
                  '[RemoteControlPage] canonical gap recovery failed',
                  error
                );
              });
            }
          }
          if (payload.type === 'canonical_event_available') {
            void history.syncDeltas().catch((error) => {
              console.warn(
                '[RemoteControlPage] canonical delta sync failed',
                error
              );
            });
          }
          if (payload.type === 'snapshot_required') {
            void history.rehydrateSnapshot().catch((error) => {
              console.warn(
                '[RemoteControlPage] snapshot recovery failed',
                error
              );
            });
          }
          if (payload.type === 'watermark' || payload.type === 'pong') {
            if (
              typeof payload.current_cursor === 'number' &&
              watermarkMatchesProject(
                payload.project_id,
                targetRef.current?.project_id
              )
            ) {
              void history
                .ensureCursor(payload.current_cursor)
                ?.catch((error) => {
                  console.warn(
                    '[RemoteControlPage] watermark recovery failed',
                    error
                  );
                });
            }
            // Command state is keyed by command_id and is independent of the
            // selected Project cursor. Never let a malformed/legacy watermark
            // suppress the only recovery path for a pending decision.
            void reconcileCommandStates().catch((error) => {
              console.warn(
                '[RemoteControlPage] command watermark recovery failed',
                error
              );
            });
          }
          if (payload.type === 'step') {
            history.recordLegacyFrame(payload);
          }
          if (payload.type === 'bridge_status') {
            setSession((current) =>
              current ? { ...current, bridge_status: payload.status } : current
            );
          }
          if (payload.type === 'session_revoked') {
            setSession((current) =>
              current
                ? { ...current, status: 'revoked', bridge_status: 'offline' }
                : current
            );
          }
          if (payload.type === 'target_changed') {
            setSession((current) =>
              current
                ? {
                    ...current,
                    current_project_id: payload.current_project_id ?? null,
                    current_task_id: payload.current_task_id ?? null,
                    current_history_id: payload.current_history_id ?? null,
                    current_brain_session_id:
                      payload.current_brain_session_id ?? null,
                  }
                : current
            );
            setAnsweredAskStepIds(new Set());
            setSelectedHumanInteractionId(null);
            humanReplyInteractionByCommandRef.current.clear();
            failedCommandStatusByIdentityRef.current.clear();
            const nextTarget = {
              project_id: (payload.current_project_id ?? null) as string | null,
              task_id: (payload.current_task_id ?? null) as string | null,
              brain_session_id: (payload.current_brain_session_id ?? null) as
                | string
                | null,
            };
            targetRef.current = nextTarget;
            if (
              nextTarget.project_id &&
              eventSocketRef.current?.readyState === WebSocket.OPEN
            ) {
              eventSocketRef.current.send(
                JSON.stringify({
                  type: 'subscribe_project',
                  project_id: nextTarget.project_id,
                })
              );
            }
            if (nextTarget.project_id) {
              void history.hydrate(nextTarget.project_id).catch((error) => {
                console.warn(
                  '[RemoteControlPage] target history load failed',
                  error
                );
              });
            }
          }
          if (payload.type === 'space_project_upserted' && payload.project) {
            setProjects((current) => [
              payload.project,
              ...current.filter((project) => project.id !== payload.project.id),
            ]);
          }
          if (payload.type === 'desktop_target_ready') {
            clearSwitchTimeout();
            setProjectLoading(false);
            setSession((current) =>
              current
                ? {
                    ...current,
                    current_project_id:
                      payload.current_project_id ?? current.current_project_id,
                    current_task_id:
                      payload.current_task_id ?? current.current_task_id,
                    current_brain_session_id:
                      payload.current_brain_session_id ??
                      current.current_brain_session_id,
                  }
                : current
            );
          }
          if (payload.type === 'desktop_target_failed') {
            clearSwitchTimeout();
            setProjectLoading(false);
            toast.error('Desktop project switch failed', {
              description:
                payload.error || payload.error_code || 'Try switching again.',
            });
            void reloadSession().catch(() => {});
          }
          if (payload.type === 'command_status') {
            console.info('[RemoteControlPage][RC-TRACE] command_status event', {
              command_id: payload.command_id,
              status: payload.status,
              error_code: payload.error_code,
              error: payload.error,
            });
            if (FAILED_COMMAND_STATUSES.has(payload.status)) {
              restoreFailedHumanReply({
                commandId: payload.command_id,
                clientRequestId: payload.client_request_id,
                humanInteractionId: payload.human_interaction_id,
                status: payload.status,
              });
              toast.error('Remote command failed on desktop', {
                description:
                  payload.error || payload.error_code || 'Unknown error',
              });
            }
            setCommands((current) =>
              mergeRemoteCommand(current, {
                id: payload.command_id,
                projectId: payload.project_id,
                runId: payload.run_id,
                clientRequestId: payload.client_request_id,
                humanInteractionId: payload.human_interaction_id,
                content: 'Remote command',
                type: 'unknown',
                status: payload.status,
                error: payload.error || payload.error_code,
              })
            );
          }
          if (payload.type === 'command_event' && payload.projection) {
            const projection = payload.projection as RemoteCommandProjection;
            const status = commandProjectionStatus(projection);
            if (FAILED_COMMAND_STATUSES.has(status)) {
              restoreFailedHumanReply({
                commandId: payload.command_id,
                clientRequestId:
                  payload.client_request_id ?? projection.client_request_id,
                humanInteractionId:
                  payload.human_interaction_id ??
                  projection.human_interaction_id,
                status,
              });
            }
            setCommands((current) =>
              mergeRemoteCommand(current, {
                id: payload.command_id,
                projectId: payload.project_id,
                runId: payload.run_id,
                clientRequestId: payload.client_request_id,
                humanInteractionId: payload.human_interaction_id,
                content: 'Remote command',
                type: 'unknown',
                status,
                lateResult: Boolean(projection.late_result),
                lastSyncedAt: new Date().toISOString(),
                error:
                  projection.integrity_alert ||
                  (status === 'failed' || status === 'rejected'
                    ? status
                    : undefined),
                projection,
              })
            );
          }
          if (
            payload.type === 'subscribed_project' &&
            payload.snapshot_required
          ) {
            void history
              .rehydrateSnapshot(payload.project_id)
              .catch((error) => {
                console.warn(
                  '[RemoteControlPage] project snapshot load failed',
                  error
                );
              });
          }
        } catch (err) {
          console.warn('[RemoteControlPage] invalid ws message', err);
        }
      };
      ws.onclose = (event) => {
        console.warn('[RemoteControlPage][RC-TRACE] events ws closed', {
          code: event?.code,
          reason: event?.reason,
          wasClean: event?.wasClean,
          stopped,
        });
        if (eventSocketRef.current === ws) eventSocketRef.current = null;
        if (pingTimer) {
          window.clearInterval(pingTimer);
          pingTimer = null;
        }
        if (watchdogTimer) {
          window.clearInterval(watchdogTimer);
          watchdogTimer = null;
        }
        if ((event?.code === 4401 || event?.code === 4403) && !stopped) {
          stopped = true;
          handleRemoteAuthError({
            message:
              event.code === 4403
                ? 'Signed in with a different Eigent account.'
                : 'Remote control sign-in is required.',
            status: event.code === 4403 ? 403 : 401,
            code:
              event.code === 4403
                ? REMOTE_LOGIN_USER_MISMATCH_CODE
                : REMOTE_LOGIN_REQUIRED_CODE,
          });
          return;
        }
        if (!stopped) {
          const base = Math.min(30_000, 3_000 * 2 ** reconnectAttempt);
          const delay = base + Math.floor(Math.random() * 1_000);
          reconnectAttempt += 1;
          isReconnect = true;
          reconnectTimer = window.setTimeout(connect, delay);
        }
      };
      ws.onerror = () => ws?.close();
    };

    connect();
    return () => {
      stopped = true;
      if (pingTimer) window.clearInterval(pingTimer);
      if (watchdogTimer) window.clearInterval(watchdogTimer);
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      ws?.close();
      if (eventSocketRef.current === ws) eventSocketRef.current = null;
    };
  }, [
    clearSwitchTimeout,
    handleRemoteAuthError,
    history.applyCanonicalEvents,
    history.ensureCursor,
    history.hydrate,
    history.needsLegacySync,
    history.recordLegacyFrame,
    history.rehydrateSnapshot,
    history.syncDeltas,
    history.syncLegacySteps,
    linkToken,
    reconcileCommandStates,
    restoreFailedHumanReply,
    sessionReady,
    sessionId,
  ]);

  useEffect(
    () => () => {
      clearSwitchTimeout();
    },
    [clearSwitchTimeout]
  );

  const selectProject = async (
    project: RemoteControlProject,
    force = false
  ) => {
    if (
      !bridgeOnline ||
      (!force && projectLoading) ||
      project.id === target?.project_id
    )
      return;
    setProjectLoading(true);
    clearSwitchTimeout();
    switchTimeoutRef.current = window.setTimeout(() => {
      switchTimeoutRef.current = null;
      setProjectLoading(false);
      toast.error('Desktop project switch timed out', {
        description:
          'The desktop did not confirm the new project in time. Try switching again.',
      });
      void reloadSession().catch(() => {});
    }, SWITCH_TARGET_TIMEOUT_MS);
    try {
      const response = await patchRemoteControlTarget(sessionId, linkToken, {
        project_id: project.id,
      });
      setSession((current) =>
        current
          ? {
              ...current,
              current_project_id: response.current_project_id,
              current_task_id: response.current_task_id ?? null,
              current_history_id: response.current_history_id ?? null,
              current_brain_session_id: response.current_brain_session_id,
            }
          : current
      );
      const nextTarget = {
        project_id: response.current_project_id,
        task_id: response.current_task_id ?? null,
        brain_session_id: response.current_brain_session_id,
      };
      targetRef.current = nextTarget;
      const historyLoad = history.hydrate(response.current_project_id);
      if (eventSocketRef.current?.readyState === WebSocket.OPEN) {
        eventSocketRef.current.send(
          JSON.stringify({
            type: 'subscribe_project',
            project_id: response.current_project_id,
          })
        );
      }
      setAnsweredAskStepIds(new Set());
      setSelectedHumanInteractionId(null);
      humanReplyInteractionByCommandRef.current.clear();
      failedCommandStatusByIdentityRef.current.clear();
      await historyLoad;
    } catch (err) {
      clearSwitchTimeout();
      setProjectLoading(false);
      const pageError = toPageError(err, 'Failed to switch project');
      if (handleRemoteAuthError(pageError)) return;
      toast.error(pageError.message, { description: errorHint(pageError) });
    }
  };

  const createProject = async (name: string) => {
    if (!name || projectLoading || !bridgeOnline) return;
    setProjectLoading(true);
    try {
      const project = await createRemoteControlProject(sessionId, linkToken, {
        name,
        mode: 'single-agent',
      });
      setProjects((current) => [
        project,
        ...current.filter((item) => item.id !== project.id),
      ]);
      await selectProject(project, true);
      toast.success('Project created');
    } catch (err) {
      clearSwitchTimeout();
      setProjectLoading(false);
      const pageError = toPageError(err, 'Failed to create project');
      if (handleRemoteAuthError(pageError)) return;
      toast.error(pageError.message, { description: errorHint(pageError) });
    }
  };

  const sendFolderOperation = async (type: 'refresh' | 'apply' | 'discard') => {
    if (!activeProject || controlLoading || !folderBacked) return;
    setControlLoading(type);
    try {
      let response;
      if (type === 'refresh') {
        response = await refreshRemoteControlProject(
          sessionId,
          linkToken,
          activeProject.id,
          { force: window.confirm('Force refresh if pending overlays exist?') }
        );
      } else if (type === 'apply') {
        const runId = window.prompt('Run ID to apply');
        if (!runId) return;
        if (!window.confirm('Apply this run to the desktop folder?')) return;
        response = await applyRemoteControlProjectRun(
          sessionId,
          linkToken,
          activeProject.id,
          { run_id: runId, confirm: true }
        );
      } else {
        if (!window.confirm('Discard pending overlays for this project?'))
          return;
        response = await discardRemoteControlProjectOverlays(
          sessionId,
          linkToken,
          activeProject.id,
          { confirm: true }
        );
      }
      if (response) {
        setCommands((current) =>
          mergeRemoteCommand(current, {
            id: response.command_id,
            content: type,
            type: `space_${type}`,
            status: response.status,
          })
        );
      }
      toast.success(`${type} command sent`);
    } catch (err) {
      const pageError = toPageError(err, `Failed to ${type} project`);
      if (handleRemoteAuthError(pageError)) return;
      toast.error(pageError.message, { description: errorHint(pageError) });
    } finally {
      setControlLoading(null);
    }
  };

  const refreshOverlaySummary = async () => {
    if (!activeProject || !folderBacked) return;
    try {
      const response = await listRemoteControlOverlays(
        sessionId,
        linkToken,
        activeProject.id
      );
      toast.info(`${response.overlays.length} pending overlay(s)`);
    } catch (err) {
      const pageError = toPageError(err, 'Failed to load overlays');
      if (handleRemoteAuthError(pageError)) return;
      toast.error(pageError.message, { description: errorHint(pageError) });
    }
  };
  void refreshOverlaySummary; // referenced only via side panel

  const submit = async (
    content: string,
    options?: {
      humanInput?: HumanInputItem | null;
      ignorePendingAsk?: boolean;
      preserveDraft?: boolean;
    }
  ) => {
    const trimmed = content.trim();
    if (
      !trimmed ||
      sendingRef.current ||
      sending ||
      !ready ||
      interactionDecisionPending
    )
      return;
    sendingRef.current = true;
    setSending(true);
    try {
      const humanInput =
        options && Object.prototype.hasOwnProperty.call(options, 'humanInput')
          ? options.humanInput || null
          : replyTarget;
      const interaction = pendingInteraction;
      const ask =
        interaction || humanInput || options?.ignorePendingAsk
          ? null
          : pendingAsk;
      if (interaction && interaction.interactionType !== 'question') {
        toast.error('Use the interaction card to continue.');
        return;
      }
      const type = interaction
        ? 'interaction_decision'
        : humanInput || ask
          ? 'human_reply'
          : 'user_message';
      const humanInputAgent = humanInput
        ? activityTimeline?.runs.find((run) => run.id === humanInput.runId)
            ?.agentExecutionsById[humanInput.agentExecutionKey]?.name || null
        : null;
      const payload: Record<string, unknown> = interaction
        ? {
            run_id: interaction.runId,
            interaction_id: interaction.interactionId,
            expected_version: interaction.version,
            action_digest: interaction.actionDigest,
            decision: { reply: trimmed },
          }
        : humanInput
          ? {
              agent: humanInputAgent || humanInput.agentExecutionId,
              reply: trimmed,
              human_interaction_id: humanInput.interactionId,
              agent_execution_id: humanInput.agentExecutionId,
            }
          : ask
            ? { agent: getAskAgent(ask), reply: trimmed }
            : { content: trimmed, attachments: [] };
      if (ask && !payload.agent) {
        toast.error('Missing agent name');
        return;
      }

      const response = await sendRemoteControlCommand(
        sessionId,
        linkToken,
        type,
        payload,
        target
      );
      if (humanInput) {
        humanReplyInteractionByCommandRef.current.set(
          response.command_id,
          humanInput.interactionId
        );
        if (response.client_request_id) {
          humanReplyInteractionByCommandRef.current.set(
            response.client_request_id,
            humanInput.interactionId
          );
        }
        while (humanReplyInteractionByCommandRef.current.size > 64) {
          const oldestKey = humanReplyInteractionByCommandRef.current
            .keys()
            .next().value;
          if (!oldestKey) break;
          humanReplyInteractionByCommandRef.current.delete(oldestKey);
        }
      }
      const earlyFailureStatus =
        failedCommandStatusByIdentityRef.current.get(response.command_id) ||
        (response.client_request_id
          ? failedCommandStatusByIdentityRef.current.get(
              response.client_request_id
            )
          : null);
      const effectiveStatus = earlyFailureStatus || response.status;
      console.info('[RemoteControlPage][RC-TRACE] command accepted by server', {
        command_id: response.command_id,
        type,
        status: effectiveStatus,
        next_task_id: response.next_task_id,
        target,
      });
      setCommands((current) =>
        mergeRemoteCommand(current, {
          id: response.command_id,
          projectId: response.project_id ?? target?.project_id,
          runId:
            response.run_id ??
            response.next_task_id ??
            humanInput?.runId ??
            (ask ? ask.task_id : null),
          clientRequestId: response.client_request_id,
          humanInteractionId:
            response.human_interaction_id ?? humanInput?.interactionId,
          content: trimmed,
          type,
          status: effectiveStatus,
          interactionId: interaction?.interactionId,
          interactionRequest: interaction ? payload : undefined,
          interactionTarget: interaction ? target : undefined,
          requestPayload: payload,
          requestTarget: target,
          createdAt: Date.now(),
        })
      );
      if (ask && !FAILED_COMMAND_STATUSES.has(effectiveStatus)) {
        setAnsweredAskStepIds((current) => {
          const next = new Set(current);
          next.add(ask.step_id);
          return next;
        });
      }
      if (humanInput && !FAILED_COMMAND_STATUSES.has(effectiveStatus)) {
        setSelectedHumanInteractionId((current) =>
          current === humanInput.interactionId ? null : current
        );
      } else if (humanInput) {
        toast.error('The reply was not accepted. You can try again.');
      }
      const replyFailed =
        Boolean(humanInput) && FAILED_COMMAND_STATUSES.has(effectiveStatus);
      if (!options?.preserveDraft && !replyFailed) {
        setMessage('');
      }
      if (interaction) {
        setMessage('');
      }
    } catch (err) {
      const pageError = toPageError(err, 'Failed to send remote command');
      if (handleRemoteAuthError(pageError)) return;
      toast.error(
        pageError.status === 429 ? 'Slow down a little' : pageError.message,
        { description: errorHint(pageError) }
      );
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const retryUserMessage = async (command: CommandStatus) => {
    if (sendingRef.current || sending || !ready) return;
    const payload = command.requestPayload || {
      content: command.content,
      attachments: [],
    };
    sendingRef.current = true;
    setSending(true);
    try {
      const response = await sendRemoteControlCommand(
        sessionId,
        linkToken,
        'user_message',
        payload,
        command.requestTarget || target
      );
      setCommands((current) => {
        const withoutFailedAttempt = current.filter(
          (item) =>
            item.id !== command.id &&
            (!command.clientRequestId ||
              item.clientRequestId !== command.clientRequestId)
        );
        return mergeRemoteCommand(withoutFailedAttempt, {
          id: response.command_id,
          projectId: response.project_id ?? command.projectId,
          runId: response.run_id ?? response.next_task_id ?? command.runId,
          clientRequestId: response.client_request_id,
          content: command.content,
          type: 'user_message',
          status: response.status,
          requestPayload: payload,
          requestTarget: command.requestTarget || target,
          createdAt: Date.now(),
        });
      });
    } catch (err) {
      const pageError = toPageError(err, 'Failed to resend the message');
      if (handleRemoteAuthError(pageError)) return;
      toast.error(pageError.message, { description: errorHint(pageError) });
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const decideInteraction = async (decision: Record<string, unknown>) => {
    const interaction = pendingInteraction;
    if (!interaction || sendingRef.current || sending || !ready) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const priorTerminal = latestSupersedableInteractionDecisionCommand(
        commandsRef.current,
        interaction.interactionId
      );
      const request = {
        run_id: interaction.runId,
        interaction_id: interaction.interactionId,
        expected_version: interaction.version,
        action_digest: interaction.actionDigest,
        decision,
        ...(priorTerminal ? { supersedes_command_id: priorTerminal.id } : {}),
      };
      const response = await sendRemoteControlCommand(
        sessionId,
        linkToken,
        'interaction_decision',
        request,
        target
      );
      setCommands((current) =>
        mergeRemoteCommand(current, {
          id: response.command_id,
          content: String(
            decision.decision || decision.option_id || 'Interaction response'
          ),
          type: 'interaction_decision',
          status: response.status,
          interactionId: interaction.interactionId,
          interactionRequest: request,
          interactionTarget: target,
          createdAt: Date.now(),
        })
      );
    } catch (err) {
      const pageError = toPageError(err, 'Failed to save the decision');
      if (handleRemoteAuthError(pageError)) return;
      toast.error(pageError.message, { description: errorHint(pageError) });
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const retryInteractionDecision = async () => {
    const interaction = pendingInteraction;
    if (!interaction || sendingRef.current || sending || !ready) return;
    const command = latestInteractionDecisionCommand(
      commandsRef.current,
      interaction.interactionId,
      interaction.version,
      interaction.actionDigest
    );
    if (!command?.interactionRequest) {
      toast.error(
        'The original decision payload is unavailable. Refresh command status instead of creating a new decision.'
      );
      await reconcileCommandStates(true);
      return;
    }
    sendingRef.current = true;
    setSending(true);
    try {
      const response = await sendRemoteControlCommand(
        sessionId,
        linkToken,
        'interaction_decision',
        command.interactionRequest,
        command.interactionTarget || target
      );
      setCommands((current) =>
        mergeRemoteCommand(current, {
          ...command,
          id: response.command_id,
          status: response.status,
          createdAt: Date.now(),
        })
      );
    } catch (err) {
      const pageError = toPageError(err, 'Failed to retry the decision');
      if (handleRemoteAuthError(pageError)) return;
      await reconcileCommandStates(true);
      toast.error(pageError.message, { description: errorHint(pageError) });
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const sendControlCommand = async (
    type: 'stop' | 'skip_task',
    label: string,
    confirmText: string
  ) => {
    if (!ready || controlLoading) return;
    if (!window.confirm(confirmText)) return;
    setControlLoading(type);
    try {
      const response = await sendRemoteControlCommand(
        sessionId,
        linkToken,
        type,
        {},
        target
      );
      setCommands((current) =>
        mergeRemoteCommand(current, {
          id: response.command_id,
          content: label,
          type,
          status: response.status,
        })
      );
      toast.success(`${label} command sent`);
    } catch (err) {
      const pageError = toPageError(
        err,
        `Failed to send ${label.toLowerCase()}`
      );
      if (handleRemoteAuthError(pageError)) return;
      toast.error(pageError.message, { description: errorHint(pageError) });
    } finally {
      setControlLoading(null);
    }
  };

  const extendSession = async () => {
    if (!session || controlLoading) return;
    setControlLoading('extend');
    try {
      const response = await extendRemoteControlSession(
        sessionId,
        linkToken,
        86400
      );
      setSession((current) =>
        current ? { ...current, expires_at: response.expires_at } : current
      );
      toast.success('Remote link extended');
    } catch (err) {
      const pageError = toPageError(err, 'Failed to extend remote link');
      if (handleRemoteAuthError(pageError)) return;
      toast.error(pageError.message, { description: errorHint(pageError) });
    } finally {
      setControlLoading(null);
    }
  };

  const revokeSession = async () => {
    if (
      !session ||
      controlLoading ||
      !window.confirm('Revoke this remote link?')
    )
      return;
    setControlLoading('revoke');
    try {
      await revokeRemoteControlSession(sessionId, linkToken);
      setSession((current) =>
        current
          ? { ...current, status: 'revoked', bridge_status: 'offline' }
          : current
      );
      toast.success('Remote link revoked');
    } catch (err) {
      const pageError = toPageError(err, 'Failed to revoke remote link');
      if (handleRemoteAuthError(pageError)) return;
      toast.error(pageError.message, { description: errorHint(pageError) });
    } finally {
      setControlLoading(null);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void submit(message);
  };
  void onSubmit; // submit is called directly from RemoteInputBox

  // ── Loading state ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <main className="flex h-[100dvh] w-screen items-center justify-center overflow-hidden bg-ds-bg-neutral-strong-default text-ds-text-neutral-default-default">
        <section className="flex h-[calc(100dvh-4px)] w-[calc(100vw-4px)] items-center justify-center rounded-[20px] bg-ds-bg-neutral-subtle-default">
          <MorphIcon
            icon={Loader2}
            className="h-5 w-5 animate-spin text-ds-icon-neutral-muted-default"
          />
        </section>
      </main>
    );
  }

  // ── Error state ──────────────────────────────────────────────────────────────
  if (error || !session) {
    const pageError = error ?? { message: 'Unable to open this remote link.' };
    const needsLogin = isRemoteLoginRequired(pageError);
    const needsDifferentLogin = isRemoteLoginMismatch(pageError);
    const isRetryable =
      !needsLogin &&
      !needsDifferentLogin &&
      (!pageError.status ||
        pageError.status >= 500 ||
        pageError.status === 409 ||
        pageError.status === 429);
    const title = needsLogin
      ? 'Sign in required'
      : needsDifferentLogin
        ? 'Wrong Eigent account'
        : 'Remote control unavailable';
    const authActionLabel = needsDifferentLogin
      ? 'Sign in with another account'
      : 'Sign in to Eigent';
    return (
      <main className="flex h-[100dvh] w-screen items-center justify-center overflow-hidden bg-ds-bg-neutral-strong-default p-0.5 text-ds-text-neutral-default-default">
        <section className="flex h-full w-full items-center justify-center rounded-[20px] bg-ds-bg-neutral-subtle-default px-4">
          <div className="w-full max-w-md rounded-2xl border border-solid border-ds-border-neutral-subtle-default bg-ds-bg-neutral-default-default p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <img src={eigentAppIconBlack} alt="" className="h-8 w-8" />
              <h1 className="text-heading-sm font-semibold">{title}</h1>
            </div>
            <p className="mt-3 text-body-sm font-medium text-ds-text-neutral-default-default">
              {pageError.message}
            </p>
            <p className="mt-2 text-body-sm text-ds-text-neutral-muted-default">
              {errorHint(pageError)}
            </p>
            {(pageError.status || pageError.code) && (
              <p className="mt-3 text-label-xs text-ds-text-neutral-muted-default">
                {pageError.status ? `HTTP ${pageError.status}` : ''}
                {pageError.status && pageError.code ? ' · ' : ''}
                {pageError.code || ''}
              </p>
            )}
            {(needsLogin || needsDifferentLogin) && (
              <div className="mt-5 flex flex-col gap-2">
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  buttonContent="text"
                  buttonRadius="full"
                  className="w-full justify-center gap-2"
                  onClick={startRemoteSignIn}
                >
                  <MorphIcon icon={LogIn} className="h-4 w-4" />
                  {authActionLabel}
                </Button>
                <p className="text-center text-label-xs text-ds-text-neutral-muted-default">
                  {needsDifferentLogin
                    ? 'Use the desktop owner account to continue.'
                    : 'You will return here after signing in.'}
                </p>
              </div>
            )}
            {isRetryable && (
              <Button
                type="button"
                variant="outline"
                size="md"
                buttonContent="text"
                buttonRadius="full"
                className="mt-4"
                onClick={() => void retryLoad()}
                disabled={reloading}
              >
                {reloading ? (
                  <MorphIcon icon={Loader2} className="h-4 w-4 animate-spin" />
                ) : (
                  <MorphIcon icon={RefreshCw} className="h-4 w-4" />
                )}
                Try again
              </Button>
            )}
          </div>
        </section>
      </main>
    );
  }

  // ── Main UI ──────────────────────────────────────────────────────────────────
  const inputPlaceholder = ready
    ? pendingInteraction
      ? pendingInteraction.interactionType === 'question'
        ? String(
            pendingInteraction.request.question ||
              pendingInteraction.request.content ||
              'Reply to the desktop question'
          )
        : 'Respond using the interaction card'
      : pendingHumanInputs.length > 1 && !replyTarget
        ? 'Send a follow-up, or select an agent question above to reply'
        : replyTarget
          ? replyTarget.question || 'Reply to the agent question'
          : pendingAsk
            ? getAskText(pendingAsk) || 'Reply to the desktop question'
            : 'Send a follow-up to the desktop task'
    : 'Select a project to start';

  return (
    <main className="flex h-[100dvh] w-screen gap-0.5 overflow-hidden bg-ds-bg-neutral-strong-default p-0.5 text-ds-text-neutral-default-default">
      {/* Left panel — project selector */}
      <RemoteSidePanel
        open={sidePanelOpen}
        onOpenChange={setSidePanelOpen}
        session={session}
        target={target}
        space={space}
        projects={projects}
        projectLoading={projectLoading}
        controlLoading={controlLoading}
        onSelectProject={(project) => void selectProject(project)}
        onCreateProject={(name) => void createProject(name)}
        folderBacked={folderBacked}
        activeProject={activeProject}
        onRefresh={() => void sendFolderOperation('refresh')}
        onApply={() => void sendFolderOperation('apply')}
        onDiscard={() => void sendFolderOperation('discard')}
        bridgeOnline={bridgeOnline}
      />

      <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[20px] bg-ds-bg-neutral-subtle-default">
        {/* Header */}
        <header className="h-12 shrink-0 px-2">
          <div className="flex h-full items-center">
            <Button
              variant="ghost"
              size="md"
              buttonContent="icon-only"
              buttonRadius="full"
              aria-label="Open remote control panel"
              onClick={() => {
                if (!isDesktop) setSettingsPanelOpen(false);
                setSidePanelOpen(true);
              }}
            >
              <MorphIcon icon={Menu} className="h-4 w-4" />
            </Button>
            <img
              src={appIcon}
              alt=""
              // Crisp, filter-free square mark; theme-aware so it contrasts with
              // the header background (dark header ≈ #1F1F1F vs the old #1D1D1D
              // square, which made the icon read as a blurry smudge).
              className="h-7 w-7 shrink-0 select-none"
              aria-hidden
            />

            <div className="flex-1" />

            {/* Bridge status pill tag */}
            <span
              className={cn(
                'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 !text-label-xs font-semibold',
                bridgeOnline
                  ? 'bg-ds-bg-status-completed-subtle-default text-ds-text-status-completed-default-default'
                  : 'bg-ds-bg-neutral-default-default text-ds-text-neutral-muted-default'
              )}
            >
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  bridgeOnline
                    ? 'bg-ds-bg-status-completed-default-default'
                    : 'bg-ds-icon-neutral-muted-default'
                )}
              />
              {bridgeOnline ? 'Connected' : 'Offline'}
            </span>

            {/* Settings */}
            <Button
              variant="ghost"
              size="md"
              buttonContent="icon-only"
              buttonRadius="full"
              aria-label="Open settings panel"
              onClick={() => {
                if (!isDesktop) setSidePanelOpen(false);
                setSettingsPanelOpen(true);
              }}
            >
              <MorphIcon icon={Settings} className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {/* Main content */}
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden pl-2">
          <div className="scrollbar scrollbar-always-visible min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2">
            <div className="mx-auto flex min-h-full w-full max-w-[720px] flex-col gap-3 pb-36 pt-4">
              {/* No target selected */}
              {!isRemoteControlTargetReady(target) ? (
                <section className="rounded-2xl bg-ds-bg-neutral-subtle-default p-6 text-center">
                  <p className="text-body-sm font-semibold text-ds-text-neutral-default-default">
                    No project selected
                  </p>
                  <p className="mt-1 text-body-sm text-ds-text-neutral-muted-default">
                    Open the menu to select a project in this Space.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    buttonContent="text"
                    buttonRadius="full"
                    className="mt-4"
                    onClick={() => setSidePanelOpen(true)}
                  >
                    <MorphIcon icon={Menu} className="h-4 w-4" />
                    Open panel
                  </Button>
                </section>
              ) : !activityTimeline ? (
                <section
                  className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl bg-ds-bg-neutral-subtle-default p-8 text-center"
                  role="status"
                >
                  <MorphIcon
                    icon={Loader2}
                    className="h-5 w-5 animate-spin text-ds-icon-neutral-muted-default"
                  />
                  <p className="mt-3 text-body-sm font-semibold text-ds-text-neutral-default-default">
                    Loading activity
                  </p>
                  <p className="mt-1 max-w-sm text-body-sm text-ds-text-neutral-muted-default">
                    Restoring the latest durable project history.
                  </p>
                </section>
              ) : (
                <RunTimeline
                  model={activityTimeline}
                  artifactsByRun={effectiveProjectView?.artifactsByRun}
                  artifactManifestsByRun={
                    effectiveProjectView?.artifactManifestsByRun
                  }
                  onOpenArtifact={openArtifact}
                  onAnswerHumanInput={(item: HumanInputItem) => {
                    if (
                      replyTarget?.interactionId !== item.interactionId &&
                      message.trim()
                    ) {
                      setMessage('');
                    }
                    setSelectedHumanInteractionId(item.interactionId);
                  }}
                  answeredHumanInteractionIds={
                    durablyAnsweredHumanInteractionIds
                  }
                />
              )}
            </div>
          </div>

          {/* Sticky bottom input — lifted above the mobile keyboard via --keyboard-inset */}
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-30 mx-auto flex max-w-[720px] justify-center rounded-t-2xl bg-ds-bg-neutral-subtle-default px-2 pb-1 transition-transform duration-200 ease-out"
            style={{
              transform: 'translateY(calc(-1 * var(--keyboard-inset, 0px)))',
            }}
          >
            <div className="pointer-events-auto w-full">
              {pendingInteraction || expiredInteraction ? (
                <RemoteHumanInteractionCard
                  interaction={pendingInteraction || expiredInteraction!}
                  pending={interactionDecisionPending}
                  retryAvailable={interactionDecisionRetryAvailable}
                  terminalProblem={interactionDecisionProblem}
                  expired={!pendingInteraction}
                  disabled={!ready || sending || interactionDecisionPending}
                  onDecision={(decision) => void decideInteraction(decision)}
                  onRetry={() => void retryInteractionDecision()}
                  onReconcile={() => void reconcileCommandStates(true)}
                  onDismiss={() =>
                    setDismissedExpiredInteractionIds((current) => {
                      const next = new Set(current);
                      next.add(
                        (pendingInteraction || expiredInteraction!)
                          .interactionId
                      );
                      saveDismissedExpired(sessionId, next);
                      return next;
                    })
                  }
                />
              ) : null}
              {/* Send-failed banner */}
              {lastCommand &&
                FAILED_COMMAND_STATUSES.has(lastCommand.status) &&
                (lastCommand.type === 'user_message' ||
                  lastCommand.type === 'human_reply') && (
                  <div className="mb-2 flex items-center justify-between gap-3 rounded-2xl border border-solid border-ds-border-error-subtle-default bg-ds-bg-error-subtle-default p-3 text-body-sm text-ds-text-error-default-default">
                    <span className="min-w-0 truncate">
                      Send failed: {lastCommand.error || lastCommand.content}
                    </span>
                    <Button
                      variant="outline"
                      tone="error"
                      size="sm"
                      buttonContent="text"
                      buttonRadius="full"
                      className="shrink-0"
                      disabled={
                        sending ||
                        (lastCommand.type === 'human_reply' &&
                          !failedHumanReplyTarget)
                      }
                      title={
                        lastCommand.type === 'human_reply' &&
                        !failedHumanReplyTarget
                          ? 'The original agent question is no longer pending.'
                          : undefined
                      }
                      onClick={() => {
                        if (lastCommand.type === 'user_message') {
                          void retryUserMessage(lastCommand);
                          return;
                        }
                        void submit(lastCommand.content, {
                          humanInput: failedHumanReplyTarget,
                          preserveDraft: true,
                        });
                      }}
                    >
                      <MorphIcon icon={RefreshCw} className="h-4 w-4" />
                      Resend
                    </Button>
                  </div>
                )}

              <RemoteInputBox
                value={message}
                onChange={(value) => {
                  if (
                    value &&
                    !message &&
                    replyTarget &&
                    selectedHumanInteractionId === null
                  ) {
                    setSelectedHumanInteractionId(replyTarget.interactionId);
                  }
                  setMessage(value);
                }}
                onSend={() => void submit(message)}
                placeholder={inputPlaceholder}
                disabled={
                  !ready ||
                  sending ||
                  interactionDecisionPending ||
                  interactionDecisionRetryAvailable ||
                  Boolean(
                    pendingInteraction &&
                    pendingInteraction.interactionType !== 'question' &&
                    !interactionDecisionProblem
                  )
                }
                agentMode={activeProject?.mode}
                replyingTo={
                  replyTarget
                    ? replyTarget.question || 'Agent question'
                    : pendingAsk
                      ? getAskText(pendingAsk)
                      : null
                }
                onCancelReply={
                  selectedHumanInput && pendingHumanInputs.length > 1
                    ? () => {
                        setSelectedHumanInteractionId(null);
                        setMessage('');
                      }
                    : undefined
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* Right panel — connection status + controls */}
      <RemoteSettingsPanel
        open={settingsPanelOpen}
        onOpenChange={setSettingsPanelOpen}
        session={session}
        target={target}
        controlLoading={controlLoading}
        onSkip={() =>
          void sendControlCommand(
            'skip_task',
            'Stop task',
            'Stop the current desktop task gracefully?'
          )
        }
        onStop={() =>
          void sendControlCommand(
            'stop',
            'Force stop',
            'Force stop the current desktop task?'
          )
        }
        onExtend={() => void extendSession()}
        onRevoke={() => void revokeSession()}
        ready={ready}
        bridgeOnline={bridgeOnline}
        sessionId={sessionId}
        linkToken={linkToken}
      />
    </main>
  );
}
